import * as Sentry from "@sentry/vue";
import { client as apollo } from "@/graphql/vue-apollo";
import createCustomerAccessTokenMutation from "@/graphql/mutations/CreateCustomerAccessToken.gql";
import customerAccessTokenCreateWithMultipass from "@/graphql/mutations/CreateCustomerAccessTokenWithMultipass.gql";
import createUserMutation from "@/graphql/mutations/CreateUser.gql";
import isEmpty from "lodash-es/isEmpty";
import add from "date-fns/add";
import customerQuery from "@/graphql/queries/Customer.gql";

const state = {
  customerAccessToken: null,
  customerEmail: null,
  customer: {
    id: null,
    email: null,
    birthday: null,
  },
  multipassAccount: {
    url: null,
    token: null,
  },
};

const mutations = {
  setCustomerAccessToken(state, value) {
    state.customerAccessToken = value;
  },
  setCustomerEmail(state, value) {
    state.customerEmail = value;
  },
  setCustomer(state, value) {
    state.customer = value;
  },
  setMultipassAccount(state, value) {
    state.multipassAccount = value;
  },
};

const getters = {
  isLoggedIn: (state) => {
    if (state.customerAccessToken) {
      return true;
    }

    return false;
  },
};

const actions = {
  checkAccessToken({ commit, dispatch }) {
    const itemStr = localStorage.getItem("shopifyCustomerAccessToken");

    if (!itemStr) {
      return null;
    }

    const item = JSON.parse(itemStr);
    const now = new Date();

    if (now.getTime() > item.expiry) {
      dispatch("attemptLogOut");
      return null;
    }

    commit("setCustomerAccessToken", item);
  },
  fetchCustomerEmail({ commit, dispatch }) {
    const itemStr = localStorage.getItem("shopifyCustomerEmail");

    if (!itemStr) {
      return null;
    }

    commit("setCustomerEmail", itemStr);
    dispatch("multipassAccount", null, { root: true });
  },
  fetchCustomer({ commit, dispatch }) {
    const itemStr = localStorage.getItem("shopifyCustomer");

    if (!itemStr) {
      return null;
    }

    const json = JSON.parse(itemStr);

    commit("setCustomer", json);
    dispatch("multipassAccount", null, { root: true });
  },
  async attemptLogin({ dispatch }, { email, password, returnUrl }) {
    const { data } = await apollo.mutate({
      mutation: createCustomerAccessTokenMutation,
      variables: {
        input: {
          email,
          password,
        },
      },
    });

    const token = data.customerAccessTokenCreate.customerAccessToken;
    if (token) {
      dispatch("setCustomerAccessToken", token);
      dispatch("setCustomerEmail", email);
      dispatch("multipassCheckoutUrl", null, { root: true });
      dispatch("getCustomerDetails");
      await dispatch("multipassAccount", returnUrl, { root: true });
    }

    return data.customerAccessTokenCreate;
  },
  async getCustomerDetails({ dispatch, state }) {
    if (!state.customerAccessToken?.value) {
      return;
    }

    const { data } = await apollo.query({
      query: customerQuery,
      variables: {
        accessToken: state.customerAccessToken.value,
      },
    });

    if (!data.customer) {
      return false;
    }

    dispatch("setCustomer", data.customer);
  },
  async attemptSSOLogin({ dispatch }, { accessToken, returnUrl }) {
    dispatch("setCustomerAccessToken", { accessToken });

    const { data } = await apollo.query({
      query: customerQuery,
      variables: {
        accessToken,
      },
    });

    if (!data.customer) {
      //customer doesn't exist
      return false;
    }

    dispatch("setCustomerEmail", data.customer.email);
    dispatch("setCustomer", data.customer);
    dispatch("multipassCheckoutUrl", null, { root: true });

    await dispatch("multipassAccount", returnUrl, { root: true });

    return true;
  },
  async attemptRegister(
    { dispatch },
    { firstName, lastName, email, password, phone }
  ) {
    const { data } = await apollo.mutate({
      mutation: createUserMutation,
      variables: {
        input: {
          firstName,
          lastName,
          email,
          password,
          phone,
          acceptsMarketing: true,
        },
      },
    });

    if (data.customerCreate.customerUserErrors.length >= 1) {
      return data.customerCreate;
    }

    return await dispatch("attemptLogin", { email, password });
  },
  attemptLogOut({ commit }) {
    localStorage.removeItem("shopifyCustomerAccessToken");
    localStorage.removeItem("shopifyCustomerEmail");
    commit("setCustomerAccessToken", null);
    commit("setCustomerEmail", null);
  },
  setCustomerAccessToken({ commit }, { accessToken, expiresAt }) {
    const sevenDayExpiry = add(new Date(), { days: 7 });

    const expiry = expiresAt
      ? new Date(expiresAt).getTime().toString()
      : sevenDayExpiry.getTime().toString();

    const item = {
      value: accessToken,
      expiry,
    };

    localStorage.setItem("shopifyCustomerAccessToken", JSON.stringify(item));

    commit("setCustomerAccessToken", item);
  },
  setCustomerEmail({ commit }, email) {
    localStorage.setItem("shopifyCustomerEmail", email);
    commit("setCustomerEmail", email);
  },
  setCustomer({ commit }, customer) {
    const string = JSON.stringify(customer);
    localStorage.setItem("shopifyCustomer", string);
    commit("setCustomer", customer);
  },
  multipassAccount: {
    root: true,
    async handler({ commit, state, dispatch }, redirectUrl) {
      const email = state.customerEmail;

      if (!email) {
        return false;
      }

      const input = {
        email: state.customerEmail,
        redirectUrl: redirectUrl || "/account",
      };

      Sentry.addBreadcrumb({
        category: "auth",
        message: "multipassAccount input",
        data: input,
        level: "info",
      });

      const multipassInputString = new URLSearchParams(input).toString();
      const multipassResponse = await fetch(
        process.env.RATANDBOA_NODE_APP_URL +
          "/multipass?" +
          multipassInputString
      );

      Sentry.addBreadcrumb({
        category: "auth",
        message: "multipassAccount response",
        data: multipassResponse,
        level: "info",
      });

      const multipassData = await multipassResponse.json();

      Sentry.addBreadcrumb({
        category: "auth",
        message: "multipassAccount json",
        data: multipassData,
        level: "info",
      });

      if (isEmpty(multipassData)) {
        Sentry.captureMessage("Empty multipass json", "error");
        return;
      }

      if (!multipassData.token) {
        Sentry.captureMessage("No multipass token", "error");
        return;
      }

      commit("setMultipassAccount", multipassData);

      const { data } = await apollo.mutate({
        mutation: customerAccessTokenCreateWithMultipass,
        variables: {
          multipassToken: multipassData.token,
        },
      });

      const token =
        data.customerAccessTokenCreateWithMultipass.customerAccessToken;

      const errors =
        data.customerAccessTokenCreateWithMultipass.customerUserErrors;
      if (errors.length >= 1) {
        Sentry.captureMessage(errors);
        errors.forEach((error) => {
          throw new Error(error.code);
        });
      }

      if (token) {
        dispatch("setCustomerAccessToken", token);
      }
    },
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
